import { useEffect, useCallback, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useFileManagerStore } from "../../../stores/fileManagerStore";
import { useShallow } from "zustand/react/shallow";
import { useDebounce, extractBackendErrorMessage } from "../../../utils/utils";
import {
  useProjectFiles,
  usePresignUpload,
  useConfirmUpload,
  useFileUrl,
  useDeleteFile,
  useCreateShareLink,
  useStorageUsage,
  useCreateFolder,
  useUpdateFolder,
  useDeleteFolder,
  useFolderTree,
  useMoveFile,
} from "../../../lib/react-query/files";
import { useProjectDetails } from "../../../lib/react-query/tasks";
import { showToast, ConfirmModal, Button, Divider, CustomTooltip } from "../../../components/shared";
import { UsageMeter } from "../../../components/Files/UsageMeter";
import { FileList } from "../../../components/Files/FileList";
import { FileUploader } from "../../../components/Files/FileUploader";
import { FileViewer } from "../../../components/Files/FileViewer";
import { FileShareModal } from "../../../components/Files/FileShareModal";
import { FileDetailsPanel } from "../../../components/Files/FileDetailsPanel";
import { FolderBreadcrumbs } from "../../../components/Files/FolderBreadcrumbs";
import { endPoints } from "../../../redux/network/constant";
import {
  FolderCreateModal,
  FolderRenameModal,
  FolderMoveModal,
  FolderDeleteModal,
} from "../../../components/Files/FolderModals";
import { HiOutlineCloudArrowUp, HiOutlineFolder } from "react-icons/hi2";
import { ProjectsIcon, ArrowHead } from "../../../assets/icons";

const ProjectFiles = ({
  projectId: projectIdOverride = null,
  projectTitle: projectTitleOverride = "",
  parentLabel = "Projects",
  parentPath = "/projects",
  backLabel = "Back",
  backPath = null,
}) => {
  const { id: idParam } = useParams();
  const projectId = projectIdOverride ?? Number(idParam);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: project } = useProjectDetails(projectId, !!projectId);
  const projectTitle = projectTitleOverride || project?.title || "—";

  const {
    searchQuery,
    sortBy,
    sortOrder,
    filterType,
    page,
    uploadQueue,
    viewModalOpen,
    viewerFile,
    shareModalOpen,
    shareFileData,
    deleteModalOpen,
    deletingFile,
    currentFolderId,
    selectedFileId,
    createFolderOpen,
    renameItem,
    moveItem,
    deleteFolder,
    setSearchQuery,
    setSortBy,
    setFilterType,
    setSort,
    setPage,
    openViewer,
    closeViewer,
    openShareModal,
    closeShareModal,
    openDeleteModal,
    closeDeleteModal,
    setCurrentFolderId,
    openCreateFolder,
    closeCreateFolder,
    openRename,
    closeRename,
    openMove,
    closeMove,
    openDeleteFolder,
    closeDeleteFolder,
    enqueueFiles,
    updateUpload,
    removeUpload,
    clearCompletedUploads,
    setSelectedFileId,
    reset,
  } = useFileManagerStore(
    useShallow((state) => ({
      searchQuery: state.searchQuery,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
      filterType: state.filterType,
      page: state.page,
      uploadQueue: state.uploadQueue,
      selectedFileId: state.selectedFileId,
      viewModalOpen: state.viewModalOpen,
      viewerFile: state.viewerFile,
      shareModalOpen: state.shareModalOpen,
      shareFileData: state.shareFileData,
      deleteModalOpen: state.deleteModalOpen,
      deletingFile: state.deletingFile,
      currentFolderId: state.currentFolderId,
      createFolderOpen: state.createFolderOpen,
      renameItem: state.renameItem,
      moveItem: state.moveItem,
      deleteFolder: state.deleteFolder,
      setSearchQuery: state.setSearchQuery,
      setSortBy: state.setSortBy,
      setFilterType: state.setFilterType,
      setSort: state.setSort,
      setPage: state.setPage,
      setSelectedFileId: state.setSelectedFileId,
      openViewer: state.openViewer,
      closeViewer: state.closeViewer,
      openShareModal: state.openShareModal,
      closeShareModal: state.closeShareModal,
      openDeleteModal: state.openDeleteModal,
      closeDeleteModal: state.closeDeleteModal,
      setCurrentFolderId: state.setCurrentFolderId,
      openCreateFolder: state.openCreateFolder,
      closeCreateFolder: state.closeCreateFolder,
      openRename: state.openRename,
      closeRename: state.closeRename,
      openMove: state.openMove,
      closeMove: state.closeMove,
      openDeleteFolder: state.openDeleteFolder,
      closeDeleteFolder: state.closeDeleteFolder,
      enqueueFiles: state.enqueueFiles,
      updateUpload: state.updateUpload,
      removeUpload: state.removeUpload,
      reset: state.reset,
    }))
  );

  const debouncedSearch = useDebounce(searchQuery, 300);
  const ordering = sortOrder === "asc" ? sortBy : `-${sortBy}`;

  // URL is the single source of truth for the current folder. Mirror URL → store.
  useEffect(() => {
    const urlFolder = searchParams.get("folder");
    const parsed = urlFolder ? Number(urlFolder) : null;
    const next = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    if (next !== currentFolderId) {
      setCurrentFolderId(next);
    }
  }, [searchParams, currentFolderId, setCurrentFolderId]);

  const navigateToFolder = useCallback(
    (id) => {
      const next = new URLSearchParams(searchParams);
      if (id != null) {
        next.set("folder", String(id));
      } else {
        next.delete("folder");
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const { data: fileData, isLoading: filesLoading, error: filesError } = useProjectFiles(
    projectId,
    { search: debouncedSearch, ordering, category: filterType, page, page_size: 50, folder_id: currentFolderId },
    !!projectId
  );

  const { data: usageData, isLoading: usageLoading } = useStorageUsage(projectId, !!projectId);
  const { data: folderTreeData } = useFolderTree(
    projectId,
    moveItem?.kind === "folder" ? moveItem.id : null,
    !!projectId && moveItem != null,
  );

  const fileInputRef = useRef(null);
  const activeUploadsRef = useRef(new Set());
  const xhrMapRef = useRef(new Map());
  const previewRequestRef = useRef(0);

  const presignMutation = usePresignUpload();
  const confirmMutation = useConfirmUpload();
  const fileUrlMutation = useFileUrl();
  const deleteMutation = useDeleteFile();
  const shareMutation = useCreateShareLink();
  const createFolderMutation = useCreateFolder();
  const updateFolderMutation = useUpdateFolder();
  const deleteFolderMutation = useDeleteFolder();
  const moveFileMutation = useMoveFile();

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFileId, setPreviewFileId] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [shareResult, setShareResult] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [modalError, setModalError] = useState(null);

  const folders = useMemo(() => fileData?.folders || [], [fileData]);
  const files = useMemo(() => fileData?.items || [], [fileData]);
  const permissions = fileData?.permissions || {};
  const usage = usageData || fileData?.usage || { used_bytes: 0, quota_bytes: 0 };
  const breadcrumbs = fileData?.breadcrumbs || [];
  const currentFolder = fileData?.current_folder || null;
  const usagePercent = usage.quota_bytes > 0 ? (usage.used_bytes / usage.quota_bytes) * 100 : 0;
  const selectedFile = viewerFile || files.find((file) => file.id === selectedFileId) || files[0] || null;
  const isUploading = uploadQueue.some((item) => item.status === "pending" || item.status === "uploading");

  useEffect(() => {
    if (moveItem && moveItem.projectId != null && moveItem.projectId !== projectId) {
      closeMove();
    }
  }, [moveItem, projectId, closeMove]);

  useEffect(() => {
    if (!selectedFileId && files.length > 0) {
      setSelectedFileId(files[0].id);
    }
    if (selectedFileId && !files.some((file) => file.id === selectedFileId)) {
      setSelectedFileId(files[0]?.id || null);
    }
  }, [files, selectedFileId, setSelectedFileId]);

  useEffect(() => {
    setSelectedFileId(null);
    reset();
  }, [projectId, reset, setSelectedFileId]);

  useEffect(() => {
    const xhrMap = xhrMapRef.current;
    const activeUploads = activeUploadsRef.current;
    return () => {
      xhrMap.forEach((xhr) => xhr.abort());
      xhrMap.clear();
      activeUploads.clear();
      reset();
    };
  }, [reset]);

  useEffect(() => {
    if (!modalError) return;
    const id = setTimeout(() => setModalError(null), 4000);
    return () => clearTimeout(id);
  }, [modalError]);

  const uploadFileToS3 = useCallback(
    (uploadItem, presignedData, index) => {
      const { id, file } = uploadItem;
      const presignedItem = presignedData.files[index];

      if (!presignedItem) {
        updateUpload(id, { status: "error", error: "Presigned URL not found for file" });
        return;
      }

      activeUploadsRef.current.add(id);
      updateUpload(id, { status: "uploading", startedAt: Date.now() });

      const formData = new FormData();
      Object.entries(presignedItem.fields).forEach(([key, value]) => formData.append(key, value));
      formData.append("file", file);

      const xhr = new XMLHttpRequest();
      xhrMapRef.current.set(id, xhr);
      let lastLoaded = 0;
      let lastTime = Date.now();

      xhr.upload.addEventListener("progress", (e) => {
        if (!e.lengthComputable) return;
        const now = Date.now();
        const elapsed = (now - lastTime) / 1000;
        const deltaBytes = e.loaded - lastLoaded;
        const speed = elapsed > 0 ? deltaBytes / elapsed : 0;
        const remaining = e.total - e.loaded;
        const eta = speed > 0 ? remaining / speed : null;
        lastLoaded = e.loaded;
        lastTime = now;
        updateUpload(id, { progress: Math.round((e.loaded / e.total) * 100), speed, eta });
      });

      xhr.addEventListener("load", async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            await confirmMutation.mutateAsync({
              projectId,
              payload: {
                s3_key: presignedItem.s3_key,
                original_name: file.name,
                content_type: file.type,
                size_bytes: file.size,
                folder_id: presignedItem.folder_id,
              },
            });
            updateUpload(id, { status: "success", progress: 100, speed: 0, eta: 0 });
          } catch (err) {
            updateUpload(id, { status: "error", error: extractBackendErrorMessage(err, "Upload confirmation failed") });
          }
        } else {
          updateUpload(id, { status: "error", error: `Upload failed (${xhr.status})` });
        }
        activeUploadsRef.current.delete(id);
        xhrMapRef.current.delete(id);
      });

      xhr.addEventListener("error", () => {
        updateUpload(id, { status: "error", error: "Connection lost during upload. Check your network and try again." });
        activeUploadsRef.current.delete(id);
        xhrMapRef.current.delete(id);
      });

      xhr.addEventListener("abort", () => {
        updateUpload(id, { status: "error", error: "Upload cancelled" });
        activeUploadsRef.current.delete(id);
        xhrMapRef.current.delete(id);
      });

      xhr.open("POST", presignedItem.upload_url);
      xhr.send(formData);
    },
    [confirmMutation, projectId, updateUpload]
  );

  useEffect(() => {
    const pendingItems = uploadQueue.filter((item) => item.status === "pending" && !activeUploadsRef.current.has(item.id));
    if (pendingItems.length === 0) return;

    pendingItems.forEach((item) => {
      activeUploadsRef.current.add(item.id);
    });

    const startUpload = async () => {
      const filesPayload = pendingItems.map((item) => ({
        original_name: item.file.name,
        content_type: item.file.type,
        size_bytes: item.file.size,
      }));

      try {
        const presignedData = await presignMutation.mutateAsync({
          projectId,
          files: filesPayload,
          folderId: currentFolderId,
        });
        pendingItems.forEach((item, idx) => uploadFileToS3(item, presignedData, idx));
      } catch (err) {
        pendingItems.forEach((item) =>
          updateUpload(item.id, {
            status: "error",
            error: extractBackendErrorMessage(err, "Failed to get upload URL"),
          })
        );
        pendingItems.forEach((item) => {
          activeUploadsRef.current.delete(item.id);
        });
      }
    };

    startUpload();
  }, [uploadQueue, presignMutation, projectId, updateUpload, uploadFileToS3, currentFolderId]);

  const handleRetry = useCallback(
    (item) => updateUpload(item.id, { status: "pending", progress: 0, error: null, speed: 0, eta: null }),
    [updateUpload]
  );

  const handleRemoveUpload = useCallback((id) => removeUpload(id), [removeUpload]);

  const loadPreviewUrl = useCallback(
    async (file) => {
      const requestId = ++previewRequestRef.current;
      setPreviewFileId(file.id);
      setPreviewUrl(null);
      setPreviewError(null);
      try {
        const url = await fileUrlMutation.mutateAsync({ projectId, fileId: file.id });
        if (requestId === previewRequestRef.current) {
          setPreviewUrl(url);
        }
        return url;
      } catch (err) {
        if (requestId === previewRequestRef.current) {
          setPreviewError(extractBackendErrorMessage(err, "Failed to load preview"));
          setPreviewUrl(null);
        }
        throw err;
      }
    },
    [fileUrlMutation, projectId]
  );

  const handlePreview = useCallback(
    async (file) => {
      setSelectedFileId(file.id);
      openViewer(file);
      try {
        await loadPreviewUrl(file);
      } catch (err) {
        showToast("Failed to load preview", "error");
      }
    },
    [loadPreviewUrl, openViewer, setSelectedFileId]
  );

  const handleLoadPreview = useCallback(
    async (file) => {
      try {
        await loadPreviewUrl(file);
      } catch (err) {
        showToast("Failed to load preview", "error");
        throw err;
      }
    },
    [loadPreviewUrl]
  );

  const handleDownload = useCallback(
    async (file) => {
      try {
        const url = await fileUrlMutation.mutateAsync({ projectId, fileId: file.id });
        const a = document.createElement("a");
        a.href = url;
        a.download = file.original_name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch {
        showToast("Failed to download file", "error");
      }
    },
    [projectId, fileUrlMutation]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingFile) return;
    try {
      await deleteMutation.mutateAsync({ projectId, fileId: deletingFile.id });
      showToast("File deleted", "success");
      closeDeleteModal();
    } catch {
      showToast("Failed to delete file", "error");
    }
  }, [deletingFile, projectId, deleteMutation, closeDeleteModal]);

  const handleShareGenerate = useCallback(
    async ({ fileId, expiryHours, expiresAt }) => {
      try {
        const result = await shareMutation.mutateAsync({ projectId, fileId, expiryHours, expiresAt });
        setShareResult(result);
      } catch (err) {
        showToast(extractBackendErrorMessage(err, "Failed to create share link"), "error");
      }
    },
    [projectId, shareMutation]
  );

  const handleCloseViewer = useCallback(() => {
    previewRequestRef.current += 1;
    closeViewer();
    setPreviewUrl(null);
    setPreviewError(null);
  }, [closeViewer]);

  const handleOpenDetails = useCallback((file) => {
    setSelectedFileId(file.id);
    setDetailsModalOpen(true);
  }, [setSelectedFileId]);

  const handleCloseDetails = useCallback(() => {
    setDetailsModalOpen(false);
  }, []);

  const handleCloseShare = useCallback(() => {
    closeShareModal();
    setShareResult(null);
  }, [closeShareModal]);

  const handleOpenFolder = useCallback(
    (folder) => {
      if (!folder) return;
      navigateToFolder(folder.id);
    },
    [navigateToFolder]
  );

  const handleBreadcrumbNavigate = useCallback(
    (folderId) => {
      navigateToFolder(folderId);
    },
    [navigateToFolder]
  );

  const handleCreateFolder = useCallback(
    async ({ name, parentId }) => {
      setModalError(null);
      try {
        await createFolderMutation.mutateAsync({ projectId, name, parentId });
        showToast("Folder created", "success");
        closeCreateFolder();
      } catch (err) {
        setModalError(extractBackendErrorMessage(err, "Failed to create folder"));
      }
    },
    [createFolderMutation, projectId, closeCreateFolder]
  );

  const handleRenameSubmit = useCallback(
    async ({ name }) => {
      if (!renameItem) return;
      setModalError(null);
      try {
        if (renameItem.kind === "folder") {
          await updateFolderMutation.mutateAsync({ projectId, folderId: renameItem.id, name });
        } else {
          showToast("File rename is not supported in v1", "info");
          closeRename();
          return;
        }
        showToast("Folder renamed", "success");
        closeRename();
      } catch (err) {
        setModalError(extractBackendErrorMessage(err, "Failed to rename"));
      }
    },
    [renameItem, updateFolderMutation, projectId, closeRename]
  );

  const handleMoveSubmit = useCallback(
    async ({ folderId }) => {
      if (!moveItem) return;
      if (moveItem.projectId != null && moveItem.projectId !== projectId) {
        setModalError("Move target is stale. Reopen the menu and try again.");
        return;
      }
      const sourceExists =
        moveItem.kind === "folder"
          ? folders.some((folder) => folder.id === moveItem.id)
          : files.some((file) => file.id === moveItem.id);
      if (!sourceExists) {
        setModalError("Move source is stale. Reopen the menu and try again.");
        return;
      }
      setModalError(null);
      try {
        if (moveItem.kind === "folder") {
          await updateFolderMutation.mutateAsync({
            projectId,
            folderId: moveItem.id,
            parentId: folderId,
          });
        } else {
          await moveFileMutation.mutateAsync({
            projectId,
            fileId: moveItem.id,
            folderId,
          });
        }
        showToast("Moved", "success");
        closeMove();
      } catch (err) {
        setModalError(extractBackendErrorMessage(err, "Failed to move"));
      }
    },
    [moveItem, folders, files, updateFolderMutation, moveFileMutation, projectId, closeMove]
  );

  const handleDeleteFolderConfirm = useCallback(async () => {
    if (!deleteFolder) return;
    setModalError(null);
    try {
      await deleteFolderMutation.mutateAsync({ projectId, folderId: deleteFolder.id });
      showToast("Folder deleted", "success");
      if (currentFolderId === deleteFolder.id) {
        const parentId = deleteFolder.parent ?? null;
        navigateToFolder(parentId);
      }
      closeDeleteFolder();
    } catch (err) {
      setModalError(extractBackendErrorMessage(err, "Failed to delete folder"));
    }
  }, [deleteFolder, deleteFolderMutation, projectId, currentFolderId, navigateToFolder, closeDeleteFolder]);

  const handleRenameFolder = useCallback(
    (folder) => openRename({ ...folder, kind: "folder" }),
    [openRename]
  );

  const handleMoveFolder = useCallback(
    (folder) =>
      openMove({
        ...folder,
        kind: "folder",
        projectId,
        currentFolderId: folder.parent ?? currentFolderId,
      }),
    [openMove, currentFolderId, projectId]
  );

  const handleMoveFile = useCallback(
    (file) => openMove({ ...file, kind: "file", projectId, currentFolderId: file.folder_id ?? currentFolderId }),
    [openMove, currentFolderId, projectId]
  );

  const handleRenameFile = useCallback(
    (file) => openRename({ ...file, kind: "file" }),
    [openRename]
  );

  if (!Number.isFinite(projectId)) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-sm text-grey">Invalid project ID</p>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-bggrey pt-4 sticky top-0 z-50 rounded-b-xl">
        <div className="bg-white rounded-xl">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0 px-4 py-3">
            <div className="font-sans flex items-center gap-3 w-full sm:w-auto">
              <ProjectsIcon width={30} height={30} className="text-black flex-shrink-0" />
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="text-sm text-grey hover:text-secondary-dark transition"
                  onClick={() => navigate(parentPath)}
                >
                  {parentLabel}
                </button>
                <span className="text-grey">/</span>
                <CustomTooltip content={projectTitle}>
                  <span className="text-sm font-semibold max-w-[150px] truncate inline-block align-bottom">{projectTitle}</span>
                </CustomTooltip>
                <span className="text-grey">/</span>
                <span className="text-sm">Files</span>
              </div>
            </div>
            <div className="font-sans flex items-center gap-3 w-full sm:w-auto justify-end">
              <button
                type="button"
                className="flex items-center gap-1 rounded-md hover:bg-lightgrey text-sm px-2 py-1"
                onClick={() => navigate(backPath || `/projects/${projectId}/tasks`)}
                aria-label="Back"
                title="Back"
              >
                <ArrowHead width={16} height={16} className="text-darkgrey" />
                <span className="hidden sm:inline">{backLabel}</span>
              </button>
              {permissions.can_manage_folders !== false && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="md"
                  icon={HiOutlineFolder}
                  onClick={openCreateFolder}
                  className="gap-2 w-full sm:w-auto justify-center"
                >
                  New Folder
                </Button>
              )}
              {permissions.can_upload !== false && (
                <Button
                  variant="filled"
                  color="secondary"
                  size="md"
                  icon={HiOutlineCloudArrowUp}
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2 w-full sm:w-auto justify-center"
                >
                  Upload Files
                </Button>
              )}
            </div>
          </div>
          <div className="px-2"><Divider /></div>
        </div>
      </div>

      {!currentFolder?.is_root && (
        <div className="mt-4">
          <FolderBreadcrumbs
            breadcrumbs={breadcrumbs}
            onNavigate={handleBreadcrumbNavigate}
            currentFolderId={currentFolderId}
          />
        </div>
      )}

      <div className="space-y-4 mt-4">

      {filesError && (
        <div className="rounded-xl border border-danger/30 bg-danger/5 p-4 text-sm text-danger">
          Failed to load files. {(filesError && filesError.response && filesError.response.data && filesError.response.data.message) || "Please try again."}
        </div>
      )}

      <div className="grid gap-4">
        <div className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <UsageMeter usedBytes={usage.used_bytes} quotaBytes={usage.quota_bytes} isLoading={usageLoading} />
            <FileUploader
              ref={fileInputRef}
              queue={uploadQueue}
              isUploading={isUploading}
              onEnqueue={enqueueFiles}
              onRetry={handleRetry}
              onRemove={handleRemoveUpload}
              onClearCompleted={clearCompletedUploads}
              canUpload={permissions.can_upload !== false}
              usagePercent={usagePercent}
            />
          </div>

          <FileList
            folders={folders}
            files={files}
            count={fileData?.count || 0}
            page={fileData?.page || page}
            totalPages={fileData?.total_pages || 1}
            isLoading={filesLoading}
            searchQuery={searchQuery}
            filterType={filterType}
            sortBy={sortBy}
            sortOrder={sortOrder}
            permissions={permissions}
            selectedFileId={selectedFile?.id}
            currentFolderName={currentFolder?.is_root ? undefined : currentFolder?.name}
            onUpload={permissions.can_upload !== false ? () => fileInputRef.current?.click() : undefined}
            onSearchChange={setSearchQuery}
            onFilterChange={setFilterType}
            onSortChange={setSortBy}
            onSortSet={setSort}
            onPageChange={setPage}
            onSelectFile={handleOpenDetails}
            onPreview={handlePreview}
            onDownload={handleDownload}
            onShare={openShareModal}
            onDelete={openDeleteModal}
            onOpenFolder={handleOpenFolder}
            onRenameItem={handleRenameFolder}
            onMoveFolder={handleMoveFolder}
            onMoveFile={handleMoveFile}
            onDeleteFolder={permissions.can_delete ? openDeleteFolder : undefined}
          />
        </div>
      </div>

      <FileDetailsPanel
        file={selectedFile}
        canShare={permissions.can_share}
        canDelete={permissions.can_delete}
        isOpen={detailsModalOpen}
        onClose={handleCloseDetails}
        onDownload={handleDownload}
        onShare={openShareModal}
        onDelete={openDeleteModal}
        onMove={permissions.can_manage_folders ? handleMoveFile : undefined}
        onRename={handleRenameFile}
        previewUrl={selectedFile?.id === previewFileId ? previewUrl : null}
        previewError={selectedFile?.id === previewFileId ? previewError : null}
        onLoadPreview={handleLoadPreview}
        projectId={projectId}
      />

      <FileViewer
        file={viewerFile}
        previewUrl={previewUrl}
        contentUrl={viewerFile ? endPoints.ProjectFileContent(projectId, viewerFile.id) : undefined}
        previewError={previewError}
        isOpen={viewModalOpen}
        onClose={handleCloseViewer}
        onDownload={() => viewerFile && handleDownload(viewerFile)}
        onShare={() => {
          if (viewerFile) {
            closeViewer();
            setPreviewUrl(null);
            previewRequestRef.current += 1;
            openShareModal(viewerFile);
          }
        }}
        canShare={permissions.can_share}
      />

      <FileShareModal
        isOpen={shareModalOpen}
        onClose={handleCloseShare}
        file={shareFileData}
        onGenerate={handleShareGenerate}
        isLoading={shareMutation.isPending}
        shareResult={shareResult}
      />

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title="Delete File"
        message={`Are you sure you want to delete "${deletingFile?.original_name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmColor="danger"
        isLoading={deleteMutation.isPending}
      />

      <FolderCreateModal
        isOpen={createFolderOpen}
        onClose={closeCreateFolder}
        onSubmit={handleCreateFolder}
        currentFolderId={currentFolderId}
        isLoading={createFolderMutation.isPending}
        errorMessage={modalError}
      />

      <FolderRenameModal
        isOpen={renameItem != null}
        onClose={closeRename}
        onSubmit={handleRenameSubmit}
        item={renameItem}
        isLoading={updateFolderMutation.isPending}
        errorMessage={modalError}
      />

      <FolderMoveModal
        isOpen={moveItem != null}
        onClose={closeMove}
        onSubmit={handleMoveSubmit}
        item={moveItem}
        tree={folderTreeData?.tree}
        isLoading={updateFolderMutation.isPending || moveFileMutation.isPending}
        errorMessage={modalError}
      />

      <FolderDeleteModal
        isOpen={deleteFolder != null}
        onClose={closeDeleteFolder}
        onConfirm={handleDeleteFolderConfirm}
        folder={deleteFolder}
        isLoading={deleteFolderMutation.isPending}
        errorMessage={modalError}
      />
    </div>
    </div>
  );
};

export default ProjectFiles;
